import BigNumber from 'bignumber.js';
import { clsx } from 'clsx';
import { CSSProperties, FocusEvent, forwardRef, Ref, useState } from 'react';
import React from 'react';
import { Text as AriaText, TextField as AriaTextField, FieldError, Input, Label } from 'react-aria-components';
import fieldStyles from '~/components/inputs/Field.module.scss';
import { formatAmountWithDecimals } from '~/utils/formatNumber';
import { cleanValue, limitDecimalPlaces, parseValue, parseValueForInput } from '~/utils/numberInputs';
import styles from './TextField.module.scss';

export interface AmountFieldProps {
  className?: string;
  dataTestId?: string;
  endAddon?: string;
  endAddonMedia?: React.ReactNode;
  errorMessage?: string;
  helpText?: string;
  hideLabel?: boolean;
  Icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  id?: string;
  isDisabled?: boolean;
  isInvalid?: boolean;
  isRequired?: boolean;
  label: string;
  maxDecimalPlaces?: number;
  name?: string;
  onClick?: (e: React.MouseEvent<Element, MouseEvent>) => void;
  onBlur?: (e: FocusEvent<Element, Element>) => void;
  onFocus?: (e: FocusEvent<Element, Element>) => void;
  onChange?: (value: string) => void;
  placeholder?: string;
  size?: 'small' | 'medium' | 'large';
  startAddon?: string;
  style?: CSSProperties;
  value: string | undefined;
  onlyPositive?: boolean;
  defaultValue?: string;
  isReadOnly?: boolean;
  inputRef?: Ref<HTMLInputElement>;
  formatWithThousands?: boolean;
}

const AmountField = forwardRef(
  (
    {
      className,
      dataTestId,
      endAddon,
      errorMessage,
      helpText,
      hideLabel,
      Icon,
      id,
      isDisabled,
      isInvalid,
      isRequired,
      label,
      maxDecimalPlaces,
      name,
      onClick,
      onBlur,
      onChange,
      placeholder,
      size = 'medium',
      startAddon,
      style,
      value,
      onlyPositive = false,
      defaultValue,
      isReadOnly,
      endAddonMedia,
      inputRef,
      onFocus,
      formatWithThousands = false,
    }: AmountFieldProps,
    ref: Ref<HTMLDivElement>,
  ) => {
    const [focused, setFocused] = useState(false);

    const groupedValue = () => {
      const parsed = value !== undefined && value !== '' ? parseValue(value) : NaN;
      return isNaN(parsed) ? '' : formatAmountWithDecimals(parsed);
    };

    const displayValue = formatWithThousands && !focused ? groupedValue() : parseValueForInput(value);

    const handleChange = (newValue: string) => {
      const cleanedValue = cleanValue(newValue, onlyPositive);
      onChange?.(cleanedValue);
    };

    const handleFocus = (e: FocusEvent<Element, Element>) => {
      if (formatWithThousands) {
        setFocused(true);
      }
      onFocus?.(e);
    };

    const handleBlur = (e: FocusEvent<Element, Element>) => {
      const inputEl = e.target as HTMLInputElement;
      const cleanedValue = cleanValue(inputEl.value, onlyPositive);
      if (formatWithThousands) {
        setFocused(false);
        const next = cleanedValue === '' || cleanedValue === '-' ? '' : BigNumber(cleanedValue).toFixed(2);
        onChange?.(next);
      } else {
        onChange?.(limitDecimalPlaces(cleanedValue, maxDecimalPlaces));
      }
      onBlur?.(e);
    };

    return (
      <AriaTextField
        defaultValue={defaultValue}
        aria-label={hideLabel ? label : undefined}
        className={clsx(fieldStyles.field, styles.inputField, className)}
        data-size={size}
        id={id}
        isDisabled={isDisabled}
        isInvalid={isInvalid || !!errorMessage}
        isRequired={isRequired}
        isReadOnly={isReadOnly}
        name={name}
        onBlur={handleBlur}
        onClick={onClick}
        onFocus={handleFocus}
        onChange={handleChange}
        style={style}
        value={displayValue}
        ref={ref}
      >
        {!hideLabel && (
          <Label>
            {label}
            {isRequired ? ' *' : null}
          </Label>
        )}
        <div className={styles.addonWrapper}>
          {startAddon && <div className={clsx(styles.addon, styles.startAddon)}>{startAddon}</div>}
          <div className={styles.inputWrapper}>
            {Icon && <Icon className={styles.icon} />}
            <Input ref={inputRef} placeholder={placeholder} data-testid={dataTestId} />
          </div>
          {(endAddon || endAddonMedia) && (
            <div className={clsx(styles.addon, styles.endAddon)}>
              <div className={styles.endAddonContainer}>
                {endAddonMedia && <div className={styles.media}>{endAddonMedia}</div>}
                {endAddon && <span className={styles.selectedDisplayLabel}>{endAddon}</span>}
              </div>
            </div>
          )}
        </div>
        {helpText && <AriaText slot="description">{helpText}</AriaText>}
        <FieldError>{errorMessage}</FieldError>
      </AriaTextField>
    );
  },
);

export default AmountField;
